import { useCallback, useEffect, useRef, useState } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { ChevronLeft, ChevronRight, Pause, PlayArrow } from '@mui/icons-material';
import { useCachedBlobUrl } from '@/hooks/use-cached-blob-url';
import { useStillFrameReadyRegistration } from '@/components/preview/still-frame-ready-context';
import { useFlowControlSimulationCueStore } from '@/state/stores/use-flow-control-simulation-cue-store';
import { NATIVE_SLIDE_PENDING_MS, useLiveSequenceIndexStore } from '@/state/stores/use-live-sequence-index-store';
import { useLocalMediaPreviewSlideStore } from '@/state/stores/use-local-media-preview-slide-store';
import { PresentationSlideJumpModal } from '@/components/preview/presentation-slide-jump-modal';
import { useLiveSlideControlContext } from '@/components/preview/live-slide-control.context';
import { useFlowControlLiveState } from '@/components/flows/detail/flow-control-live-state';
import { useLiveContextStateWithLivePlayState } from '@/hooks/use-live-play-state';
import { isMediaAgentBackupSlidesActive } from '@/helpers/media-agent-recovery.helper';
import { useControlSlideMutation } from '@/state/mutations/player/use-control-slide';
import { prefetchPreviewBlobEntries } from '@/helpers/preview/preview-blob-cache.helper';
import { resolveLiveSlidePage, shouldSyncLiveSlideIndexFromLayer } from '@/helpers/resolve-live-slide-page.helper';
import { isOnAirCueSequenceItem } from '@/helpers/resolve-live-sequence-item-ids.helper';
import { seedLocalMediaPreviewFromDurableResume } from '@/helpers/resolve-start-slide-index-for-take.helper';
import {
  buildPresentationDeckScope,
  buildPresentationSlideCacheKey,
  clampPresentationPage,
  orderSlideIndicesForPrefetch,
  parseDwellMs,
  parseImageSequenceUrls,
  resolvePresentationSlideIndexAfterAction,
  shouldUseNativeSlideStep,
} from '@/components/preview/preview-presentation-slides-layer.helpers';
import type { PreviewPresentationSlidesLayerProps } from '@/components/preview/preview-presentation-slides-layer.types';
import { parseFrameSlideNumbers, resolvePresentationStepPosition } from '@/helpers/presentation-step-position.helper';
import { parsePresentationVideoOverlays } from '@/helpers/preview/presentation-video-overlays.helper';
import {
  createSequenceFrameHistory,
  extrapolateLiveAutoLoopStep,
  livePreviewStepWindow,
  livePreviewVideoStarts,
  localPreviewStepWindow,
  restartSequenceFrameDwell,
  sequenceStepProgress,
} from '@/helpers/preview/sequence-step-hold.helper';
import { useTranslations } from '@/i18n/use-translations';
import type { TranslationKey } from '@/i18n/translation-key';
import { PreviewPresentationVideoOverlays } from '@/components/preview/preview-presentation-video-overlays';

export function PreviewPresentationSlidesLayer(props: PreviewPresentationSlidesLayerProps) {
  const assetVersion = props.data.assetVersion;
  const itemId = props.itemId;
  return <PresentationSlidePreviewBody key={`${itemId ?? 'preview'}:${assetVersion ?? '0'}`} {...props} />;
}

function PresentationSlidePreviewBody({
  data,
  itemId: itemIdProp,
  componentId,
  slideNavigationScope = 'local',
  sequencePlaybackControls,
  controlsOnly = false,
  preferAbsoluteSlideGoto = false,
  listenForSlideKeys = true,
  onMediaEnded,
  playVideoOverlays = false,
  previewAudioEnabled = false,
}: PreviewPresentationSlidesLayerProps) {
  const t = useTranslations();
  const preparing = data.preparing === 'true';
  const rejected = data.rejected === 'true';
  const slideCount = data.slideCount ? Number.parseInt(data.slideCount, 10) : undefined;
  const assetVersion = data.assetVersion;
  const slidePreviewUrls = parseImageSequenceUrls(data);
  const frameSlideNumbers = parseFrameSlideNumbers(data.frameSlideNumbers);
  const dwellMs = parseDwellMs(data);
  const isSequencedLayer = componentId === 'imageSequence' || componentId === 'presentationSlides';
  const autoLoopFromData = isSequencedLayer && data.autoLoop === 'true';
  const autoAdvanceFromData = isSequencedLayer && data.autoAdvance === 'true';

  const itemId =
    itemIdProp ??
    (data.mediaId ? (data.mediaId.startsWith('media:') ? data.mediaId : `media:${data.mediaId}`) : undefined);
  const layerCurrentIndex = data.currentIndex ? Number.parseInt(data.currentIndex, 10) : undefined;

  const simulationPage = useFlowControlSimulationCueStore((s) => (itemId ? (s.slideIndexByItemId[itemId] ?? 1) : 1));
  const reportSimulationPageCount = useFlowControlSimulationCueStore((s) => s.reportSlidePageCount);
  const stepSimulationSlide = useFlowControlSimulationCueStore((s) => s.stepSlide);
  const setSimulationSlideIndex = useFlowControlSimulationCueStore((s) => s.setSlideIndex);
  const simulationTotalPages = useFlowControlSimulationCueStore((s) =>
    itemId ? s.totalPagesByItemId[itemId] : undefined,
  );

  const mediaId = itemId?.startsWith('media:') ? itemId.slice('media:'.length) : itemId;
  const liveIndexFromStore = useLiveSequenceIndexStore((s) => (itemId ? s.indexByItemId[itemId] : undefined));
  const setLiveSequenceIndex = useLiveSequenceIndexStore((s) => s.setIndex);
  const markNativePending = useLiveSequenceIndexStore((s) => s.markNativePending);
  const clearNativePending = useLiveSequenceIndexStore((s) => s.clearNativePending);
  const nativePendingAt = useLiveSequenceIndexStore((s) => (itemId ? s.nativePendingAtByItemId[itemId] : undefined));
  const nativePending = useLiveSequenceIndexStore((s) => (itemId ? s.isNativePending(itemId) : false));
  const liveIndex = resolveLiveSlidePage({
    storeIndex: liveIndexFromStore,
    layerCurrentIndex,
  });
  const liveTotalPages = useLiveSequenceIndexStore((s) => (itemId ? s.totalPagesByItemId[itemId] : undefined));
  const reportLiveTotalPages = useLiveSequenceIndexStore((s) => s.reportTotalPages);

  const localPreviewPage = useLocalMediaPreviewSlideStore((s) => (mediaId ? (s.indexByMediaId[mediaId] ?? 1) : 1));
  const localPreviewTotalPages = useLocalMediaPreviewSlideStore((s) =>
    mediaId ? s.totalPagesByMediaId[mediaId] : undefined,
  );
  const stepLocalPreviewSlide = useLocalMediaPreviewSlideStore((s) => s.stepSlide);
  const setLocalPreviewSlideIndex = useLocalMediaPreviewSlideStore((s) => s.setSlideIndex);
  const reportLocalPreviewTotalPages = useLocalMediaPreviewSlideStore((s) => s.reportTotalPages);
  const resetLocalPreviewMedia = useLocalMediaPreviewSlideStore((s) => s.resetMedia);

  const [jumpModalOpen, setJumpModalOpen] = useState(false);
  const [isPlaying, setIsPlaying] = useState(autoLoopFromData);
  const [dwellProgress, setDwellProgress] = useState(0);
  const [stepClockKeySeen, setStepClockKeySeen] = useState<string | null>(null);
  const frameHistoryRef = useRef(createSequenceFrameHistory());
  const liveStepTiming = useLiveSequenceIndexStore((s) => (itemId ? s.stepTimingByItemId[itemId] : undefined));
  const liveServerNowMs = useCallback(() => Date.now() + useLiveSequenceIndexStore.getState().serverOffsetMs, []);
  const liveSlideControl = useLiveSlideControlContext();
  const { mutate: controlSlide } = useControlSlideMutation();
  const useSimulationNav = slideNavigationScope === 'simulation' && Boolean(itemId);
  const useLiveNav = slideNavigationScope === 'live' && Boolean(itemId);
  const useLocalNav = slideNavigationScope === 'local' && Boolean(mediaId);
  const canLiveSlideControl = useLiveNav && Boolean(liveSlideControl) && Boolean(mediaId);
  const { liveContextState, cueState } = useFlowControlLiveState();
  const extrapolateLiveAutoLoop = isOnAirCueSequenceItem(itemId, cueState?.currentStep?.itemId);
  const liveContextMerged = useLiveContextStateWithLivePlayState(
    liveSlideControl ? `${liveSlideControl.entityType}:${liveSlideControl.entityId}` : undefined,
    liveContextState,
  );
  const backupSlidesActive = isMediaAgentBackupSlidesActive(liveContextMerged?.playState);
  const useNativeSlideStep = shouldUseNativeSlideStep({
    canLiveSlideControl,
    preferAbsoluteSlideGoto,
    backupSlidesActive,
  });

  useEffect(() => {
    if (!itemId || nativePendingAt == null) {
      return;
    }
    const remaining = NATIVE_SLIDE_PENDING_MS - (Date.now() - nativePendingAt);
    if (remaining <= 0) {
      clearNativePending(itemId);
      return;
    }
    const timer = window.setTimeout(() => clearNativePending(itemId), remaining);
    return () => window.clearTimeout(timer);
  }, [itemId, nativePendingAt, clearNativePending]);

  // Local PreviewModal: keep local store aligned with durable resume when live store already has it.
  useEffect(() => {
    if (!useLocalNav || !itemId) {
      return;
    }
    seedLocalMediaPreviewFromDurableResume(itemId);
  }, [useLocalNav, itemId]);

  const totalPages = useLiveNav
    ? liveTotalPages
    : useSimulationNav
      ? simulationTotalPages
      : useLocalNav
        ? localPreviewTotalPages
        : undefined;

  // Prefer this layer's deck size so a stale store total from another deck cannot inflate the modal.
  const effectiveTotal = slidePreviewUrls.length > 0 ? slidePreviewUrls.length : (slideCount ?? totalPages ?? 0);
  const enableLocalAutoLoop =
    slideNavigationScope !== 'live' &&
    autoLoopFromData &&
    effectiveTotal > 1 &&
    !preparing &&
    !rejected &&
    slidePreviewUrls.length > 0;
  const enableSimulationPlayThrough =
    slideNavigationScope === 'simulation' &&
    autoAdvanceFromData &&
    effectiveTotal >= 1 &&
    !preparing &&
    !rejected &&
    slidePreviewUrls.length > 0;
  const canSequencePlayback = enableLocalAutoLoop;
  const canPlayThrough = enableSimulationPlayThrough;
  const showPlaybackControls = canSequencePlayback && sequencePlaybackControls !== false;
  const showLiveDwellProgress =
    useLiveNav && autoLoopFromData && effectiveTotal > 1 && !preparing && !rejected && slidePreviewUrls.length > 0;
  const showDwellProgressBar = canSequencePlayback || showLiveDwellProgress;
  const pageNumber = useLiveNav ? liveIndex : useSimulationNav ? simulationPage : useLocalNav ? localPreviewPage : 1;
  const effectivePage = clampPresentationPage(pageNumber, effectiveTotal);
  const sequenceMediaKey = `${itemId ?? ''}:${assetVersion ?? ''}`;
  const stepClockKey = `${sequenceMediaKey}:${effectivePage}:${autoLoopFromData ? 1 : 0}`;
  if (stepClockKeySeen !== stepClockKey) {
    setStepClockKeySeen(stepClockKey);
    setDwellProgress(0);
  }
  if (!showDwellProgressBar && dwellProgress !== 0) {
    setDwellProgress(0);
  }
  const stepFrameSlideNumbers = frameSlideNumbers?.length === effectiveTotal ? frameSlideNumbers : undefined;
  const stepPosition = resolvePresentationStepPosition(effectivePage, stepFrameSlideNumbers);
  const pageLabel = !stepPosition
    ? `${effectivePage} / ${effectiveTotal}`
    : stepPosition.stepCount > 1
      ? t('media.slideControl.slideStepChip' as TranslationKey, {
          slide: stepPosition.slideNumber,
          slides: stepPosition.slideTotal,
          step: stepPosition.step,
          steps: stepPosition.stepCount,
        })
      : `${stepPosition.slideNumber} / ${stepPosition.slideTotal}`;
  const slideUrl = slidePreviewUrls[effectivePage - 1];
  const cacheKey = buildPresentationSlideCacheKey(itemId, assetVersion, effectivePage);
  const deckScope = itemId ? buildPresentationDeckScope(itemId, assetVersion) : undefined;

  useEffect(() => {
    if (!itemId || rejected || slidePreviewUrls.length === 0 || !deckScope) {
      return;
    }

    const orderedIndices = orderSlideIndicesForPrefetch(slidePreviewUrls.length, effectivePage);
    const entries = orderedIndices
      .map((index) => {
        const url = slidePreviewUrls[index];
        const key = buildPresentationSlideCacheKey(itemId, assetVersion, index + 1);
        if (!url || !key) {
          return undefined;
        }
        return { url, cacheKey: key };
      })
      .filter((entry): entry is { url: string; cacheKey: string } => Boolean(entry));

    void prefetchPreviewBlobEntries(entries, { deckScope });
  }, [itemId, assetVersion, deckScope, slidePreviewUrls, preparing, rejected, effectivePage]);

  const executeLiveSlideAction = useCallback(
    (action: 'next' | 'prev' | 'first' | 'last' | 'goto', slideNumber?: number) => {
      if (!canLiveSlideControl || !liveSlideControl || !mediaId || !itemId) {
        return;
      }
      const sendNativeStep = useNativeSlideStep && (action === 'next' || action === 'prev');
      if (sendNativeStep) {
        markNativePending(itemId);
        controlSlide(
          {
            mediaId,
            entityType: liveSlideControl.entityType,
            entityId: liveSlideControl.entityId,
            action,
          },
          {
            onError: (error) => {
              clearNativePending(itemId);
              console.error('[LiveSlideControl] Error:', error);
            },
          },
        );
        return;
      }
      const total = effectiveTotal;
      const previousPage = effectivePage;
      const optimisticPage = resolvePresentationSlideIndexAfterAction(action, previousPage, total, slideNumber);
      const didOptimisticUpdate = optimisticPage !== previousPage;
      if (didOptimisticUpdate) {
        setLiveSequenceIndex(itemId, optimisticPage, { optimistic: true });
        // Mirror local so item-preview → Play resolve still sees the choice if live hydrate lags.
        if (mediaId) {
          setLocalPreviewSlideIndex(mediaId, optimisticPage, { operatorPick: true });
        }
      } else if (itemId) {
        clearNativePending(itemId);
      }
      // Next preview / jump / first / last stay absolute so the agent does not start slide video.
      controlSlide(
        {
          mediaId,
          entityType: liveSlideControl.entityType,
          entityId: liveSlideControl.entityId,
          action: 'goto',
          slideNumber: optimisticPage,
        },
        {
          onError: (error) => {
            // Keep the optimistic page. Look-ahead Next decks often are not the agent's current
            // file yet, so control-slide fails — the selection must still apply on the next take.
            console.error('[LiveSlideControl] Error:', error);
          },
        },
      );
    },
    [
      canLiveSlideControl,
      useNativeSlideStep,
      liveSlideControl,
      mediaId,
      itemId,
      effectiveTotal,
      effectivePage,
      setLiveSequenceIndex,
      setLocalPreviewSlideIndex,
      markNativePending,
      clearNativePending,
      controlSlide,
    ],
  );

  const goToSlide = useCallback(
    (page: number) => {
      if (!effectiveTotal) {
        return;
      }
      const target = clampPresentationPage(page, effectiveTotal);
      if (canLiveSlideControl) {
        void executeLiveSlideAction('goto', target);
        return;
      }
      if (useLiveNav && itemId) {
        // No control context — still keep stores so Play/take can send startSlideIndex.
        setLiveSequenceIndex(itemId, target, { optimistic: true });
        if (mediaId) {
          setLocalPreviewSlideIndex(mediaId, target, { operatorPick: true });
        }
        return;
      }
      if (useSimulationNav && itemId) {
        setSimulationSlideIndex(itemId, target);
        return;
      }
      if (useLocalNav && mediaId) {
        setLocalPreviewSlideIndex(mediaId, target, { operatorPick: true });
      }
    },
    [
      effectiveTotal,
      canLiveSlideControl,
      useLiveNav,
      useSimulationNav,
      useLocalNav,
      itemId,
      mediaId,
      executeLiveSlideAction,
      setLiveSequenceIndex,
      setSimulationSlideIndex,
      setLocalPreviewSlideIndex,
    ],
  );

  const goSlide = useCallback(
    (delta: number) => {
      if (!effectiveTotal) {
        return;
      }
      if (canLiveSlideControl) {
        if (useNativeSlideStep) {
          void executeLiveSlideAction(delta > 0 ? 'next' : 'prev');
          return;
        }
        const target = clampPresentationPage(effectivePage + delta, effectiveTotal);
        void executeLiveSlideAction('goto', target);
        return;
      }
      if (useLiveNav && itemId) {
        const target = clampPresentationPage(effectivePage + delta, effectiveTotal);
        setLiveSequenceIndex(itemId, target, { optimistic: true });
        if (mediaId) {
          setLocalPreviewSlideIndex(mediaId, target, { operatorPick: true });
        }
        return;
      }
      if (useSimulationNav && itemId) {
        stepSimulationSlide(itemId, delta);
        return;
      }
      if (useLocalNav && mediaId) {
        stepLocalPreviewSlide(mediaId, delta);
      }
    },
    [
      effectiveTotal,
      effectivePage,
      canLiveSlideControl,
      useNativeSlideStep,
      useLiveNav,
      useSimulationNav,
      useLocalNav,
      itemId,
      mediaId,
      executeLiveSlideAction,
      setLiveSequenceIndex,
      setLocalPreviewSlideIndex,
      stepSimulationSlide,
      stepLocalPreviewSlide,
    ],
  );

  const advanceSequence = useCallback(() => {
    if (!effectiveTotal) {
      return;
    }
    if (canPlayThrough) {
      if (effectivePage >= effectiveTotal) {
        onMediaEnded?.();
        return;
      }
      setDwellProgress(0);
      goToSlide(effectivePage + 1);
      return;
    }
    if (!canSequencePlayback || !isPlaying) {
      return;
    }
    const nextPage = effectivePage >= effectiveTotal ? 1 : effectivePage + 1;
    setDwellProgress(0);
    goToSlide(nextPage);
  }, [
    canPlayThrough,
    canSequencePlayback,
    isPlaying,
    effectiveTotal,
    effectivePage,
    goToSlide,
    onMediaEnded,
    setDwellProgress,
  ]);

  useEffect(() => {
    const runLocal = (canSequencePlayback && isPlaying) || canPlayThrough;
    if (preparing || rejected || !effectiveTotal || (!runLocal && !showLiveDwellProgress)) {
      return;
    }

    let rafId = 0;
    const tick = () => {
      const now = Date.now();
      if (showLiveDwellProgress) {
        const window = livePreviewStepWindow({
          dwellMs,
          frameIndex: effectivePage,
          store: liveStepTiming,
          layerChangedAt: data.sequenceChangedAt,
          layerHoldUntil: data.sequenceHoldUntil,
          layerCurrentIndex: data.currentIndex,
          layerVideoAnchors: data.sequenceVideoAnchors,
        });
        if (!window) return;
        const serverNow = now + useLiveSequenceIndexStore.getState().serverOffsetMs;
        setDwellProgress(sequenceStepProgress(serverNow, window.enteredAtMs, window.holdUntilMs));
        if (serverNow < window.holdUntilMs) {
          rafId = requestAnimationFrame(tick);
          return;
        }
        // Players advance on the server dwell clock. If that step event has not moved the
        // on-air Current preview yet, follow it here. Next uses the same live scope and must
        // not write the shared index: that write is not an operator pick for the next take.
        if (extrapolateLiveAutoLoop && itemId && autoLoopFromData && effectiveTotal > 1) {
          const store = useLiveSequenceIndexStore.getState();
          const published = store.indexByItemId[itemId];
          if (published == null || published === effectivePage) {
            const nextPage = effectivePage >= effectiveTotal ? 1 : effectivePage + 1;
            // An already-expired window must not fast-forward the whole deck in one burst.
            const enteredAt = Math.max(window.holdUntilMs, serverNow);
            const step = extrapolateLiveAutoLoopStep({
              dwellMs: Math.max(dwellMs, 1),
              nextIndex: nextPage,
              enteredAtMs: enteredAt,
              nowMs: serverNow,
              videoOverlays: data.videoOverlays,
              previousVideoStartedAt: window.videoStartedAt,
            });
            store.setIndex(itemId, nextPage);
            store.setStepTiming(itemId, {
              index: nextPage,
              enteredAt: step.enteredAt,
              holdUntil: step.holdUntil,
              videoStartedAt: step.videoStartedAt,
            });
          }
        }
        return;
      }

      const window = localPreviewStepWindow({
        dwellMs,
        frameIndex: effectivePage,
        nowMs: now,
        videoOverlays: data.videoOverlays,
        history: frameHistoryRef.current,
        mediaKey: sequenceMediaKey,
      });
      setDwellProgress(sequenceStepProgress(now, window.enteredAtMs, window.holdUntilMs));
      if (now >= window.holdUntilMs) {
        advanceSequence();
        return;
      }
      rafId = requestAnimationFrame(tick);
    };

    rafId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafId);
  }, [
    canSequencePlayback,
    canPlayThrough,
    isPlaying,
    preparing,
    rejected,
    effectiveTotal,
    dwellMs,
    effectivePage,
    advanceSequence,
    showLiveDwellProgress,
    extrapolateLiveAutoLoop,
    autoLoopFromData,
    itemId,
    sequenceMediaKey,
    data.videoOverlays,
    data.sequenceChangedAt,
    data.sequenceHoldUntil,
    data.currentIndex,
    data.sequenceVideoAnchors,
    liveStepTiming,
  ]);

  useEffect(() => {
    setIsPlaying(autoLoopFromData || autoAdvanceFromData);
  }, [itemId, assetVersion, autoLoopFromData, autoAdvanceFromData]);

  useEffect(() => {
    if (!itemId || !slideCount || slideCount <= 0) {
      return;
    }
    if (useLiveNav) {
      reportLiveTotalPages(itemId, slideCount);
      return;
    }
    if (useSimulationNav) {
      reportSimulationPageCount(itemId, slideCount);
      return;
    }
    if (useLocalNav && mediaId) {
      reportLocalPreviewTotalPages(mediaId, slideCount);
    }
  }, [
    itemId,
    mediaId,
    slideCount,
    useLiveNav,
    useSimulationNav,
    useLocalNav,
    reportLiveTotalPages,
    reportSimulationPageCount,
    reportLocalPreviewTotalPages,
  ]);

  useEffect(() => {
    if (!itemId) {
      return;
    }
    // Only sync an explicit layer currentIndex — never invent 1 and clobber a resumed store index.
    const explicitLayerIndex =
      layerCurrentIndex != null && Number.isFinite(layerCurrentIndex) && layerCurrentIndex > 0
        ? layerCurrentIndex
        : undefined;

    if (useLiveNav) {
      const liveStore = useLiveSequenceIndexStore.getState();
      const syncPage = shouldSyncLiveSlideIndexFromLayer({
        storeIndex: liveStore.indexByItemId[itemId],
        layerCurrentIndex: explicitLayerIndex,
        recentlyOptimistic: liveStore.isRecentlyOptimistic(itemId),
      });
      if (syncPage != null) {
        setLiveSequenceIndex(itemId, syncPage);
      }
      if (slideCount && slideCount > 0) {
        reportLiveTotalPages(itemId, slideCount);
      }
      return;
    }

    if (useSimulationNav) {
      // Preserve operator-chosen page across Next/Now remounts; only seed when unset.
      const existing = useFlowControlSimulationCueStore.getState().slideIndexByItemId[itemId];
      if (existing == null && explicitLayerIndex != null) {
        setSimulationSlideIndex(itemId, explicitLayerIndex);
      }
      if (slideCount && slideCount > 0) {
        reportSimulationPageCount(itemId, slideCount);
      }
      return;
    }

    if (useLocalNav && mediaId) {
      resetLocalPreviewMedia(mediaId);
      const fromLive = itemId ? useLiveSequenceIndexStore.getState().indexByItemId[itemId] : undefined;
      const startPage =
        explicitLayerIndex ?? (typeof fromLive === 'number' && fromLive > 0 ? fromLive : undefined) ?? 1;
      setLocalPreviewSlideIndex(mediaId, startPage);
      if (slideCount && slideCount > 0) {
        reportLocalPreviewTotalPages(mediaId, slideCount);
      }
    }
  }, [
    itemId,
    mediaId,
    slideCount,
    layerCurrentIndex,
    useLiveNav,
    useSimulationNav,
    useLocalNav,
    assetVersion,
    setLiveSequenceIndex,
    reportLiveTotalPages,
    setSimulationSlideIndex,
    reportSimulationPageCount,
    resetLocalPreviewMedia,
    setLocalPreviewSlideIndex,
    reportLocalPreviewTotalPages,
  ]);

  useEffect(() => {
    if (preparing || !listenForSlideKeys || (useLiveNav && !canLiveSlideControl)) {
      return;
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
        event.preventDefault();
        goSlide(-1);
      } else if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
        event.preventDefault();
        goSlide(1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [preparing, listenForSlideKeys, useLiveNav, canLiveSlideControl, goSlide]);

  const showSlideNav = !useLiveNav || canLiveSlideControl;
  const canGoPrev = showSlideNav && effectiveTotal > 0 && (useNativeSlideStep || effectivePage > 1);
  const canGoNext = showSlideNav && effectiveTotal > 0 && (useNativeSlideStep || effectivePage < effectiveTotal);

  if (!controlsOnly && (rejected || (!slidePreviewUrls.length && preparing))) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          bgcolor: '#000',
        }}
      >
        <Typography variant="body2" color={rejected ? 'error' : 'text.secondary'}>
          {rejected ? 'Presentation processing was rejected' : 'Preparing…'}
        </Typography>
      </Box>
    );
  }

  if (controlsOnly && effectiveTotal <= 0) {
    return null;
  }

  const interactivePointerSx = controlsOnly ? ({ pointerEvents: 'auto' } as const) : undefined;

  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        position: 'relative',
        overflow: 'hidden',
        bgcolor: controlsOnly ? 'transparent' : '#000',
        ...(controlsOnly ? { pointerEvents: 'none' } : {}),
      }}
    >
      {!controlsOnly ? <PresentationSlideImage url={slideUrl} cacheKey={cacheKey} /> : null}
      {playVideoOverlays ? (
        <PreviewPresentationVideoOverlays
          raw={data.videoOverlays}
          frameIndex={effectivePage}
          audible={previewAudioEnabled}
          startedAtByVideoId={
            useLiveNav
              ? livePreviewVideoStarts({
                  frameIndex: effectivePage,
                  videos: parsePresentationVideoOverlays(data.videoOverlays)?.videos ?? [],
                  store: liveStepTiming,
                  layerChangedAt: data.sequenceChangedAt,
                  layerCurrentIndex: data.currentIndex,
                  layerVideoAnchors: data.sequenceVideoAnchors,
                })
              : undefined
          }
          clipHistoryRef={useLiveNav ? undefined : frameHistoryRef}
          clipMediaKey={sequenceMediaKey}
          serverNowMs={useLiveNav ? liveServerNowMs : undefined}
        />
      ) : null}
      {canGoPrev && !jumpModalOpen ? (
        <IconButton
          aria-label="Previous slide"
          onClick={() => goSlide(-1)}
          size="large"
          sx={{ ...slideNavButtonSx('left', controlsOnly), ...interactivePointerSx }}
        >
          <ChevronLeft fontSize="large" />
        </IconButton>
      ) : null}
      {canGoNext && !jumpModalOpen ? (
        <IconButton
          aria-label="Next slide"
          onClick={() => goSlide(1)}
          size="large"
          sx={{ ...slideNavButtonSx('right', controlsOnly), ...interactivePointerSx }}
        >
          <ChevronRight fontSize="large" />
        </IconButton>
      ) : null}
      {showPlaybackControls ? (
        <IconButton
          aria-label={isPlaying ? 'Pause sequence' : 'Play sequence'}
          onClick={() => {
            if (!isPlaying) {
              restartSequenceFrameDwell(frameHistoryRef.current, effectivePage, Date.now());
              setDwellProgress(0);
            }
            setIsPlaying((playing) => !playing);
          }}
          size="large"
          sx={{ ...sequencePlayPauseButtonSx(), ...interactivePointerSx }}
        >
          {isPlaying ? <Pause fontSize="large" /> : <PlayArrow fontSize="large" />}
        </IconButton>
      ) : null}
      {showDwellProgressBar ? (
        <SequenceDwellProgressBar active={showLiveDwellProgress ? true : isPlaying} progress={dwellProgress} />
      ) : null}
      {effectiveTotal > 0 ? (
        <>
          <Box
            component={showSlideNav ? 'button' : 'div'}
            type={showSlideNav ? 'button' : undefined}
            onClick={showSlideNav ? () => setJumpModalOpen(true) : undefined}
            aria-label={stepPosition ? pageLabel : `Slide ${effectivePage} of ${effectiveTotal}`}
            aria-busy={nativePending || undefined}
            sx={{
              position: 'absolute',
              bottom: showDwellProgressBar ? 22 : 16,
              left: '50%',
              transform: 'translateX(-50%)',
              zIndex: controlsOnly ? 10 : 2,
              ...interactivePointerSx,
              m: 0,
              px: 1.5,
              py: 0.5,
              minHeight: 28,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              appearance: 'none',
              border: 'none',
              borderRadius: 1,
              cursor: showSlideNav ? 'pointer' : 'default',
              bgcolor: 'rgba(255,255,255,0.9)',
              color: 'rgba(0,0,0,0.75)',
              fontSize: '0.875rem',
              fontFamily: 'inherit',
              fontWeight: 500,
              lineHeight: 1.43,
              whiteSpace: 'nowrap',
              userSelect: 'none',
              boxSizing: 'border-box',
              ...(showSlideNav && {
                '&:hover': {
                  bgcolor: '#fff',
                  boxShadow: '0 1px 4px rgba(0,0,0,0.18)',
                },
              }),
              ...(nativePending && {
                '@keyframes slideIndexPendingPulse': {
                  '0%, 100%': { opacity: 1 },
                  '50%': { opacity: 0.35 },
                },
                'animation': 'slideIndexPendingPulse 1s ease-in-out infinite',
              }),
            }}
          >
            {pageLabel}
          </Box>
          {showSlideNav ? (
            <PresentationSlideJumpModal
              open={jumpModalOpen}
              onClose={() => setJumpModalOpen(false)}
              currentPage={effectivePage}
              totalPages={effectiveTotal}
              onJump={goToSlide}
              slidePreviewUrls={slidePreviewUrls}
              itemId={itemId}
              assetVersion={assetVersion}
              frameSlideNumbers={stepFrameSlideNumbers}
            />
          ) : null}
        </>
      ) : null}
    </Box>
  );
}

function slideNavButtonSx(side: 'left' | 'right', controlsOnly = false) {
  return {
    'position': 'absolute',
    'top': '50%',
    [side]: 8,
    'transform': 'translateY(-50%)',
    'zIndex': controlsOnly ? 10 : 2,
    'color': '#fff',
    'bgcolor': 'rgba(0,0,0,0.45)',
    '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
  } as const;
}

function sequencePlayPauseButtonSx() {
  return {
    'position': 'absolute',
    'top': '50%',
    'left': '50%',
    'transform': 'translate(-50%, -50%)',
    'zIndex': 2,
    'color': '#fff',
    'bgcolor': 'rgba(0,0,0,0.45)',
    '&:hover': { bgcolor: 'rgba(0,0,0,0.65)' },
  } as const;
}

function SequenceDwellProgressBar({ active, progress }: { active: boolean; progress: number }) {
  const clampedProgress = Math.min(1, Math.max(0, progress));

  return (
    <Box
      aria-hidden
      sx={{
        position: 'absolute',
        left: 0,
        right: 0,
        bottom: 0,
        height: 3,
        zIndex: 3,
        bgcolor: 'rgba(0,0,0,0.07)',
        pointerEvents: 'none',
      }}
    >
      <Box
        sx={{
          height: '100%',
          width: `${clampedProgress * 100}%`,
          bgcolor: active ? 'rgba(0,0,0,0.38)' : 'transparent',
          transition: active ? 'none' : 'width 120ms ease-out',
        }}
      />
    </Box>
  );
}

function PresentationSlideImage({ url, cacheKey }: { url?: string; cacheKey?: string }) {
  const { url: blobUrl, loading, error } = useCachedBlobUrl(url, cacheKey);
  const settleReady = useStillFrameReadyRegistration(Boolean(url) && !error);
  const slideImgRef = useRef<HTMLImageElement>(null);
  const slideReadySentRef = useRef(false);

  useEffect(() => {
    slideReadySentRef.current = false;
  }, [blobUrl]);

  useEffect(() => {
    const img = slideImgRef.current;
    if (!img || !settleReady) {
      return;
    }
    const signalReady = () => {
      if (slideReadySentRef.current) {
        return;
      }
      slideReadySentRef.current = true;
      settleReady();
    };
    if (img.complete && img.naturalWidth > 0) {
      signalReady();
      return;
    }
    img.addEventListener('load', signalReady);
    img.addEventListener('error', signalReady);
    return () => {
      img.removeEventListener('load', signalReady);
      img.removeEventListener('error', signalReady);
    };
  }, [blobUrl, settleReady]);

  if (error) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          bgcolor: '#000',
        }}
      >
        <Typography variant="body2" color="error">
          Failed to load slide preview
        </Typography>
      </Box>
    );
  }

  if (!url) {
    return (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '100%',
          height: '100%',
          bgcolor: '#000',
        }}
      >
        <Typography variant="body2" color="text.secondary">
          No slide preview available
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'absolute', inset: 0 }}>
      {loading && !blobUrl ? (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: '#000',
          }}
        >
          <Typography variant="body2" color="text.secondary">
            Loading slide…
          </Typography>
        </Box>
      ) : null}
      {blobUrl ? (
        <img
          ref={slideImgRef}
          src={blobUrl}
          alt="Presentation slide"
          style={{
            width: '100%',
            height: '100%',
            objectFit: 'contain',
            objectPosition: 'center',
            display: 'block',
          }}
        />
      ) : null}
    </Box>
  );
}
